/* eslint-disable react-refresh/only-export-components */
import { useState } from 'react';
import { Tooltip, DropdownMenu } from '@radix-ui/themes';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import Text from '../../../../shared/Text';
import { DRAFT, SCHEDULED } from '../../utils/constants';
import { StatusBadge } from '../../shared/StatusBadge.jsx';
import { ScheduleModal } from '../ScheduleModal.jsx';
import { ConfirmationModal } from '../ConfirmationModal.jsx';
import {
  useCancelScheduledMutation,
  useRescheduleMutation,
} from '../../hooks/useBroadcastMessagesMutation';
import { deleteBroadcastMessage, copyBroadcastMessage } from '../../hooks/data';
import { useNotification } from '../../../../shared/Notification/useNotification';
import styles from './BroadcastTableRow.module.css';

const DateCell = ({ row }) => {
  const isDraft = row.status === DRAFT;
  const isScheduled = row.status === SCHEDULED;
  const displayDate = isDraft
    ? row.draftDate
    : isScheduled
    ? row.scheduledAt
    : row.sentAt;

  if (!displayDate) {
    return (
      <Text variant="small2" color="black.100">
        -
      </Text>
    );
  }

  return (
    <div className={styles.dateWrapper}>
      {isScheduled && (
        <i className={`fa-regular fa-clock ${styles.clockIcon}`} />
      )}
      <Text variant="small2" color="black.100">
        {displayDate}
      </Text>
    </div>
  );
};

export const AudienceNameCell = ({ audienceNames }) => {
  const displayName = audienceNames || 'All Contacts';
  const needsTooltip = displayName.length > 20;

  if (needsTooltip) {
    return (
      <Tooltip content={displayName}>
        <Text
          variant="small2"
          color="black.100"
          className={styles.truncatedText}
        >
          {displayName}
        </Text>
      </Tooltip>
    );
  }

  return (
    <Text variant="small2" color="black.100">
      {displayName}
    </Text>
  );
};

const ActionsCell = ({ row }) => {
  const { t } = useTranslation('broadcastMessages');
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { cancelScheduledMutation } = useCancelScheduledMutation();
  const { rescheduleMutation } = useRescheduleMutation({
    onSuccessCallback: () => setShowScheduleModal(false),
  });

  const { mutate: deleteBroadcast } = useMutation(deleteBroadcastMessage, {
    onSuccess: () => {
      queryClient.invalidateQueries(['broadcastMessages']);
      showSuccess({ content: t('deleteSuccess') });
      setShowDeleteModal(false);
    },
  });

  const { mutate: duplicateBroadcast } = useMutation(copyBroadcastMessage, {
    onSuccess: () => {
      queryClient.invalidateQueries(['broadcastMessages']);
      showSuccess({ content: t('duplicateSuccess') });
    },
    onError: () => {
      showError({ content: t('duplicateError') });
    },
  });

  const isDraft = row.status === DRAFT;
  const isScheduled = row.status === SCHEDULED;

  const handleEditSchedule = e => {
    e.stopPropagation();
    e.preventDefault();
    setShowScheduleModal(true);
  };

  const handleCancelSchedule = e => {
    e.stopPropagation();
    e.preventDefault();
    cancelScheduledMutation({ id: row.uuid });
  };

  const handleReschedule = ({ scheduledDate, scheduledHour }) => {
    rescheduleMutation({
      id: row.uuid,
      scheduledDate,
      scheduledHour,
    });
  };

  const handleDeleteClick = e => {
    e.stopPropagation();
    e.preventDefault();
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    deleteBroadcast({ id: row.uuid });
  };

  const handleDuplicate = e => {
    e.stopPropagation();
    e.preventDefault();
    duplicateBroadcast({ id: row.uuid });
  };

  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <div
            className={styles.actionsButtonHolder}
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <i
              className={`fa-regular fa-ellipsis-v fa-2x ${styles.actionsIcon}`}
            />
          </div>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content>
          {isScheduled && (
            <>
              <DropdownMenu.Item onClick={handleEditSchedule}>
                <i
                  className={`fa-regular fa-pen-to-square ${styles.menuIcon}`}
                />
                {t('actions.editSchedule')}
              </DropdownMenu.Item>
              <DropdownMenu.Item onClick={handleCancelSchedule}>
                <i
                  className={`fa-regular fa-calendar-xmark ${styles.menuIcon}`}
                />
                {t('actions.cancelSchedule')}
              </DropdownMenu.Item>
            </>
          )}
          <DropdownMenu.Item onClick={handleDuplicate}>
            <i className={`fa-regular fa-copy ${styles.menuIcon}`} />
            {t('actions.duplicate')}
          </DropdownMenu.Item>
          {isDraft && (
            <DropdownMenu.Item onClick={handleDeleteClick}>
              <i className={`fa-regular fa-trash ${styles.menuIcon}`} />
              {t('actions.delete')}
            </DropdownMenu.Item>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Root>

      <ScheduleModal
        open={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        onSchedule={handleReschedule}
        isEdit
      />

      <ConfirmationModal
        header={t('deleteConfirmationModal.header')}
        body={t('deleteConfirmationModal.body')}
        cancelText={t('deleteConfirmationModal.cancel')}
        submitText={t('deleteConfirmationModal.submit')}
        open={showDeleteModal}
        variant="error"
        onClose={() => setShowDeleteModal(false)}
        onSave={handleConfirmDelete}
      />
    </>
  );
};

export const broadcastTableRow = ({ broadcastMessages }) => {
  return broadcastMessages.map(row => ({
    info: {
      dataTileId: row.uuid,
      uuid: row.uuid,
      clickable: true,
      hasDivider: true,
      className: '',
      dataHref:
        row.status === DRAFT
          ? `/edit/${row.currentStep || 'review'}/${row.uuid}?from=broadcasts`
          : `/${row.uuid}?from=broadcasts`,
    },
    cells: [
      {
        content: (
          <Text variant="small2" color="primary.100">
            {row.broadcastTypeLabel}
          </Text>
        ),
        className: styles.firstCell,
        clickable: true,
        shouldLinkTo: true,
      },
      {
        content: (
          <Text variant="small2" color="black.100">
            {row.contentPreview}
          </Text>
        ),
        className: styles.baseCell,
        clickable: true,
        shouldLinkTo: true,
      },
      {
        content: <DateCell row={row} />,
        className: styles.baseCell,
        clickable: true,
        shouldLinkTo: true,
      },
      {
        content: (
          <>
            <StatusBadge status={row.status} />
          </>
        ),
        className: styles.baseCell,
        clickable: true,
        shouldLinkTo: true,
      },
      {
        content: <AudienceNameCell audienceNames={row.audienceNames} />,
        className: styles.audienceNameCell,
        clickable: true,
        shouldLinkTo: true,
      },
      {
        content: (
          <Text variant="small2" color="black.100">
            {row.totalAudience || '-'}
          </Text>
        ),
        className: styles.baseCell,
        clickable: true,
        shouldLinkTo: true,
      },
      {
        content: <ActionsCell row={row} />,
        className: styles.actionsCell,
        clickable: false,
        shouldLinkTo: false,
      },
    ],
  }));
};
